// CSS
import './Sidebar.css';
// React hooks
import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
// Skybase hooks
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
// Skybase component
import FullScreenModal from '../../components/Modals/FullScreenModal/FullScreenModal';
// External hooks/components
import { useClerk, UserButton } from '@clerk/react';
import { useMediaQuery } from 'react-responsive';
// Media assets
import HamburgerMenuIcon from '../../assets/StandardIcons/hamburger-menu-icon.svg';
import searchIcon from '../../assets/StandardIcons/search-icon-transparent.png';
import historyIcon from '../../assets/StandardIcons/rewind-icon-black.svg';
import directoryIcon from '../../assets/StandardIcons/FolderIcons/folder-icon-outline.png';
import bookmarkIcon from '../../assets/StandardIcons/BookmarkIcons/bookmark-icon-black-outline-transparent.png';
import databaseIcon from '../../assets/StandardIcons/DatabaseIcons/database-icon-outline.png';
import CloseIcon from '../../assets/StandardIcons/close-icon-black.svg';


// =======
// Sidebar
// =======
function Sidebar({
    isHamburgerAtBottom,        // bool – should the Hamburger toggle be located at the BOTTOM of the screen?
}){
    
    // destructure global context
    const { activeEngineJson, changeActiveEngineId, availableEnginesJson, pinnedEngineJson } = useData();
    const { activeUserJson } = useAuth();

    // init hooks
    const navigate = useNavigate('');
    const clerk = useClerk();
    const location = useLocation();

    // track profile modal state
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

    // responsive CSS
    const isDesktop = useMediaQuery({ minWidth: 1025 });

    // ======================
    // handleHamToggleClick() – toggles open/close the Selection Menu
    // ======================
    const [isSelectionMenuOpen, setIsSelectionMenuOpen] = useState(false);
    const handleHamToggleClick = () => {
        
        // toggle state var
        setIsSelectionMenuOpen((prev) => !prev);
    }
    // onChange ('isDesktop') – immediately reset (close) selection menu if isDesktop
    useEffect(() => {
        if (isDesktop) {
            setIsSelectionMenuOpen(false);
        }
    }, [isDesktop]);
    // onPinnedButtonClick() – run 'changeActiveEngineId' and immmediately close selection menu
    const onPinnedButtonClick = (engineID) => {
        
        changeActiveEngineId(engineID);
        setIsSelectionMenuOpen(false);
    }



    // =====================
    // handlePageButtonClick(desiredPage: String) – redirects the User to the selected page (OR, just closes this modal if the User is already on their desired page)
    // =====================
    const handlePageButtonClick = (desiredPage) => {
        
        // compare exact strings (prevents the '/' page edge case err)
        if (location.pathname !== desiredPage) {
            
            // immediately redirect to the different page
            navigate(`../${desiredPage}`);
        } else {

            // or, just close the selection menu IFF already on desired page
            setIsSelectionMenuOpen(false);
        }
    }




    // JSX Component
    return(
        <>
            {/* === (normal) === */}
            <div className='Sidebar'>
                
                {/* L: Sidebar */}
                <div className='Sidebar-sidebar'>
                    {/* "Skybase" title */}
                    <div className='Sidebar-sidebar-skybase' onClick={() => navigate('../')}>Skybase</div>

                    {/* Active database */}
                    <div className='Sidebar-sidebar-active-database'>{activeEngineJson?.name ?? "No database selected"}</div>

                    {/* Operation buttons container */}
                    <div className='Sidebar-sidebar-operation-buttons-container'>
                        
                        {/* "New search" button */}
                        <div className={`Sidebar-sidebar-operation-buttons-container-button ${window.location.pathname.includes('search') ? 'active' : ''}`} onClick={() => navigate('../search')}>
                            <div className='Sidebar-sidebar-operation-buttons-container-button-icon'><img className='Sidebar-sidebar-operation-buttons-container-button-icon--img' src={searchIcon}/></div>
                            <div className='Sidebar-sidebar-operation-buttons-container-button-title'>New search</div>
                            {window.location.pathname.includes('search') && <div className='Sidebar-sidebar-operation-buttons-container-button--active-dot'></div>}
                        </div>

                        {/* "Search history" button */}
                        <div className={`Sidebar-sidebar-operation-buttons-container-button ${window.location.pathname.includes('history') ? 'active' : ''}`} onClick={() => navigate('../history')}>
                            <div className='Sidebar-sidebar-operation-buttons-container-button-icon'>
                                <img className='Sidebar-sidebar-operation-buttons-container-button-icon--img sidebar-icon-invert' src={historyIcon}/>
                            </div>
                            <div className='Sidebar-sidebar-operation-buttons-container-button-title'>Search history</div>
                            {window.location.pathname.includes('history') && <div className='Sidebar-sidebar-operation-buttons-container-button--active-dot'></div>}
                        </div>

                        {/* "Directory" button */}
                        <div className={`Sidebar-sidebar-operation-buttons-container-button ${window.location.pathname.includes('directory') ? 'active' : ''}`} onClick={() => navigate('../directory')}>
                            <div className='Sidebar-sidebar-operation-buttons-container-button-icon'>
                                <img className='Sidebar-sidebar-operation-buttons-container-button-icon--img sidebar-icon-invert' src={directoryIcon}/>
                            </div>
                            <div className='Sidebar-sidebar-operation-buttons-container-button-title'>Directory</div>
                            {window.location.pathname.includes('directory') && <div className='Sidebar-sidebar-operation-buttons-container-button--active-dot'></div>}
                        </div>

                        {/* "Bookmarks" button */}
                        <div className={`Sidebar-sidebar-operation-buttons-container-button ${window.location.pathname.includes('bookmark') ? 'active' : ''}`} onClick={() => navigate('../bookmarks')}>
                            <div className='Sidebar-sidebar-operation-buttons-container-button-icon'>
                                <img className='Sidebar-sidebar-operation-buttons-container-button-icon--img sidebar-icon-invert' src={bookmarkIcon}/>
                            </div>
                            <div className='Sidebar-sidebar-operation-buttons-container-button-title'>Bookmarks</div>
                            {window.location.pathname.includes('bookmarks') && <div className='Sidebar-sidebar-operation-buttons-container-button--active-dot'></div>}
                        </div>

                        {/* "Find a database" button */}
                        <div className={`Sidebar-sidebar-operation-buttons-container-button ${window.location.pathname.includes('db') ? 'active' : ''}`} onClick={() => navigate('../db')}>
                            <div className='Sidebar-sidebar-operation-buttons-container-button-icon'>
                                <img className='Sidebar-sidebar-operation-buttons-container-button-icon--img sidebar-icon-invert' src={databaseIcon}/>
                            </div>
                            <div className='Sidebar-sidebar-operation-buttons-container-button-title'>Find a database</div>
                            {window.location.pathname.includes('db') && <div className='Sidebar-sidebar-operation-buttons-container-button--active-dot'></div>}
                        </div>
                    </div>

                    {/* "Pinned Databases" title */}
                    <div className='Sidebar-sidebar-pinned-buttons-container--title'>Pinned Databases</div>

                    {/* Pinned buttons container */}
                    <div className='Sidebar-sidebar-pinned-buttons-container'>
                        { pinnedEngineJson?.map( (engineJson, index) => (
                            <div key={index} className={`Sidebar-sidebar-pinned-buttons-container-button ${engineJson?.id === activeEngineJson?.id ? 'active' : ''}`} id={engineJson?.id} onClick={() => changeActiveEngineId(engineJson.id)}>
                                <div className='Sidebar-sidebar-pinned-buttons-container-button--title'>
                                    {engineJson?.name}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Profile Card */}
                    <div className='Sidebar-sidebar-profile-card'>
                        
                        {/* @clerk Profile Card */}
                        <div className='Sidebar-sidebar-profile-card-picture-column'>
                            <UserButton/>
                        </div>

                        {/* Postgres CurrentUser details */}
                        <div className='Sidebar-sidebar-profile-card-details-column'>
                            <div className='Sidebar-sidebar-profile-card-details-column--name'>{activeUserJson?.first_name} {activeUserJson?.last_name} {!activeUserJson && "Server connection failed."}</div>
                            <div className='Sidebar-sidebar-profile-card-details-column--email'>{activeUserJson?.email} {!activeUserJson && "Server connection failed."}</div>
                        </div>

                    </div>
                </div>

            </div>

            {/* === (hamburger) === */}
            <div className={`SidebarHamburger ${isHamburgerAtBottom ? 'at-bottom' : ''}`}>

                {/* Ham toggle */}
                { !isSelectionMenuOpen && <div className='SidebarHamburger-ham-toggle' onClick={() => handleHamToggleClick()}>
                        
                        {/* ham toggle icon */}
                        <img src={HamburgerMenuIcon} className='SidebarHamburger-ham-toggle-img'/>

                </div>}

            </div>

            {/* === (selection menu) == */}
            <div className={`SidebarHamburger-selection-menu ${isSelectionMenuOpen ? 'is-open' : ''}`}>
                
                {/* header */}
                <div className='SidebarHamburger-selection-menu-header'>

                    {/* close button */}
                    <div onClick={() => handleHamToggleClick()} className='SidebarHamburger-selection-menu-header-close-button'>
                        
                        {/* close button icon */}
                        <img src={CloseIcon} className='SidebarHamburger-selection-menu-header-close-button-icon'/>

                    </div>

                    {/* Company name */}
                    <div className='SidebarHamburger-selection-menu-company-name' onClick={() => navigate('../')}>Skybase</div>


                    {/* profile content */}
                    <div className='SidebarHamburger-selection-menu-header-profile-content'>

                        {/* @clerk user button */}
                        <div className='SidebarHamburger-selection-menu-header-profile-content-user-button'><UserButton/></div>

                    </div>

                </div>
                

                {/* item repeater */}
                <div className='SidebarHamburger-selection-menu-repeater'>

                    {/* /search */}
                    <div onClick={() => handlePageButtonClick('/search')} className='SidebarHamburger-selection-menu-repeater-item'>
                        <div className='SidebarHamburger-selection-menu-icon'><img className='SidebarHamburger-selection-menu-icon--img' src={searchIcon}/></div>
                        New search
                    </div>

                    {/* /history */}
                    <div onClick={() => handlePageButtonClick('/history')} className='SidebarHamburger-selection-menu-repeater-item'>
                        <div className='SidebarHamburger-selection-menu-icon'><img className='SidebarHamburger-selection-menu-icon--img sidebar-icon-invert' src={historyIcon}/></div>
                        Search history
                    </div>

                    {/* /directory */}
                    <div onClick={() => handlePageButtonClick('/directory')} className='SidebarHamburger-selection-menu-repeater-item'>
                        <div className='SidebarHamburger-selection-menu-icon'><img className='SidebarHamburger-selection-menu-icon--img sidebar-icon-invert' src={directoryIcon}/></div>
                        Directory
                    </div>

                    {/* /bookmarks */}
                    <div onClick={() => handlePageButtonClick('/bookmarks')} className='SidebarHamburger-selection-menu-repeater-item'>
                        <div className='SidebarHamburger-selection-menu-icon'><img className='SidebarHamburger-selection-menu-icon--img sidebar-icon-invert' src={bookmarkIcon}/></div>
                        Bookmarks
                    </div>

                    {/* /db */}
                    <div onClick={() => handlePageButtonClick('/db')} className='SidebarHamburger-selection-menu-repeater-item'>
                        <div className='SidebarHamburger-selection-menu-icon'><img className='SidebarHamburger-selection-menu-icon--img sidebar-icon-invert' src={databaseIcon}/></div>
                        Find a database
                    </div>

                </div>

                {/* pinned buttons title */}
                <div className='SidebarHamburger-selection-menu-pinned-buttons-title'>Pinned databases</div>

                {/* pinned buttons container */}
                <div className='SidebarHamburger-selection-menu-pinned-buttons-repeater'>
                    { pinnedEngineJson?.map( (engineJson, index) => (
                        <div key={index} onClick={() => onPinnedButtonClick(engineJson.id)} className={`SidebarHamburger-selection-menu-pinned-button-item ${engineJson?.id === activeEngineJson?.id ? 'active' : ''}`} id={engineJson?.id} >
                                <span className="SidebarHamburger-selection-menu-pinned-button-item--text">{engineJson?.name}</span>
                        </div>
                    ))}
                </div>
            </div>

        </>
    )
};


// Export statement
export default Sidebar;